/**
 * Web-side client for the MTG sets catalog cache (TNK-129).
 *
 * Mirrors `resolveSetCodeByName` in setNameResolver.ts but routes
 * through the cached `/api/mtg/sets/by-name` endpoint instead of
 * hitting Scryfall directly. Identical session-cache shape so the
 * call sites in cardSetBackfill stay drop-in interchangeable
 * (`resolveSetCodeByName` for Pokémon, `resolveMtgSetCodeByName` for
 * Magic). The server handles the upstream — first miss seeds the
 * whole Scryfall catalog (~700 rows, ~200KB), subsequent calls hit
 * the DB.
 *
 * Returns the lowercase Scryfall set code (e.g. `"ice"`, `"5ed"`,
 * `"bng"`) or null when no match exists. Negative results are cached
 * within the session so a typo doesn't keep round-tripping.
 */

import { apiFetch } from "@/lib/apiClient";

const codeCache = new Map<string, string | null>();
const codeInflight = new Map<string, Promise<string | null>>();

interface MtgSetRow {
  code: string;
  name: string;
  releasedAt: string | null;
  setType: string | null;
  cardCount: number | null;
  parentSetCode: string | null;
  iconSvgUri: string | null;
  scryfallUri: string | null;
  fetchedAt: string;
}

export async function resolveMtgSetCodeByName(
  setName: string,
): Promise<string | null> {
  const name = setName.trim();
  if (!name) return null;
  const key = name.toLowerCase();

  const cached = codeCache.get(key);
  if (cached !== undefined) return cached;
  const existing = codeInflight.get(key);
  if (existing) return existing;

  const p = (async (): Promise<string | null> => {
    try {
      const res = await apiFetch(
        `/api/mtg/sets/by-name?name=${encodeURIComponent(name)}`,
      );
      if (!res.ok) return null;
      const json = (await res.json()) as { set?: MtgSetRow | null };
      const code = json.set?.code ?? null;
      codeCache.set(key, code);
      return code;
    } catch {
      // Transient — don't cache.
      return null;
    } finally {
      codeInflight.delete(key);
    }
  })();
  codeInflight.set(key, p);
  return p;
}
